<!-- lib/components/Hero.svelte -->
<script>
  import { _ } from "svelte-i18n";
  import Button from "lib/components/Button.svelte";
  import Card from "lib/components/Card.svelte";
  import Container from "lib/components/Container.svelte";
  import Grid from "lib/components/Grid.svelte";

  // Props for customization - fallback to translated defaults
  export let title = "";
  export let subtitle = "";
  export let ctaText = "";
  export let ctaLink = "/register";
  export let imageUrl = "../../images/v2/phone.webp";
  export let imageAlt = "";

  // Use reactive statements to provide translated defaults when props are not provided
  $: displayTitle = title || $_("hero.defaultTitle");
  $: displaySubtitle = subtitle || $_("hero.defaultSubtitle");
  $: displayCtaText = ctaText || $_("hero.defaultCta");
  $: displayImageAlt = imageAlt || $_("hero.imageAlt");

  // Info cards data with translation keys
  const infoCards = [
    {
      icon: "🛡️",
      titleKey: "hero.card1.title",
      descriptionKey: "hero.card1.description",
    },
    {
      icon: "⚡",
      titleKey: "hero.card2.title",
      descriptionKey: "hero.card2.description",
    },
    {
      icon: "📊",
      titleKey: "hero.card3.title",
      descriptionKey: "hero.card3.description",
    },
  ];
</script>

<Container class="hero-container">
  <div class="hero-main">
    <div class="hero-content">
      <h1 class="hero-title">{displayTitle}</h1>
      <p class="hero-subtitle">{@html displaySubtitle}</p>
      <Button href={ctaLink} variant="primary" size="large">
        {displayCtaText}
      </Button>
    </div>
    <div class="hero-image-container">
      <a href={ctaLink} aria-label={displayCtaText}>
        <img
          src={imageUrl}
          alt={displayImageAlt}
          class="hero-image faded-edges"
        />
      </a>
    </div>
  </div>


  <div class="utility-header">
    <h2 class="utility-title">{$_("home.utility.title")}</h2>
    <p class="utility-subtitle">{$_("home.utility.subtitle")}</p>
  </div>

  <Grid columns="3" gap="2rem" class="info-cards">
    {#each infoCards as card}
      <Card variant="glass" alignment="center">
        <div class="card-icon" slot="icon">{card.icon}</div>
        <h3 slot="title">{$_(card.titleKey)}</h3>
        <p slot="description">{$_(card.descriptionKey)}</p>
      </Card>
    {/each}
  </Grid>
</Container>

<style lang="scss">
  
  .utility-header {
    text-align: center;
    margin: 0 auto 2.5rem auto;
    max-width: 720px;
  }

  .utility-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: var(--text-color1);
    margin: 0 0 0.75rem 0;
  }

  .utility-subtitle {
    font-size: 1.05rem;
    line-height: 1.6;
    color: var(--text-color2);
    margin: 0;
  }

.hero-main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4rem;
    margin-bottom: 5rem;
  }

  .hero-content {
    flex: 0 0 auto;
    max-width: 600px;
    z-index: 2;
  }

  .hero-title {
    font-size: 4rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    line-height: 1.1;
    color: var(--text-color1);
  }

  .hero-subtitle {
    font-size: 1.3rem;
    line-height: 1.6;
    color: var(--text-color2);
    margin-bottom: 2.5rem;
    font-weight: 400;
  }

  .hero-image-container {
    flex: 1 1 auto;
    max-width: 500px;
    min-width: 350px;
  }

  .hero-image {
    width: 100%;
    max-width: 400px;
    height: auto;
    filter: brightness(1.3);
  }

  .faded-edges {
    -webkit-mask-image: linear-gradient(
        to top,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 1) 10%
      ),
      linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 10%),
      linear-gradient(to left, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 5%),
      linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 30%);
    mask-image: linear-gradient(
        to top,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 1) 10%
      ),
      linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 10%),
      linear-gradient(to left, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 5%),
      linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 30%);
    -webkit-mask-composite: intersect;
    mask-composite: intersect;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
  }

  :global(.card-icon) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    transition: all 0.3s ease;
    filter: hue-rotate(0deg) saturate(0.7) brightness(1.1);
  }

  :global(.card:hover .card-icon) {
    filter: none;
    transform: scale(1.1);
  }
  @media (max-width: 1024px) {
    :global(.info-cards) {
      grid-template-columns: repeat(2, 1fr) !important;
    }
  }

  @media (max-width: 768px) {
    .hero-main {
      flex-direction: column;
      text-align: center;
      gap: 0rem;
      margin-bottom: 1.5rem;
    }

    .hero-title {
      font-size: 2.5rem;
    }

    .hero-image-container {
      margin-top: 0;
      transform: none; /* Remove the scale(0.9) */
      max-width: none;
      width: 100%;
      align-self: center;
      max-height: 600px;
    }

    .hero-image {
      min-width: 400px;
      max-width: 450px;
      width: 120%; /*wider than container */
      margin-left: -10%; /* Center by offsetting */
      margin: 0 auto;
      display: block;
      margin-top: -20px;
    }

    :global(.info-cards) {
      grid-template-columns: 1fr !important;
    }
  }

  @media (max-width: 480px) {
    .hero-main {
      gap: 0rem;
      margin-bottom: 0rem;
    }

    .hero-title {
      font-size: 2rem;
      margin-bottom: 1rem;
    }

    .hero-subtitle {
      font-size: 1.1rem;
      margin-bottom: 2rem;
    }

    .hero-image-container {
      transform: scale(0.85);
      max-width: 280px;
      max-height: 500px;
    }

    .hero-image {
      max-width: 260px;
      margin-top: -40px;
    }
  }

  @media (max-width: 320px) {
    .hero-title {
      font-size: 1.75rem;
    }

    .hero-image-container {
      transform: scale(0.8);
      max-width: 240px;
    }

    .hero-image {
      max-width: 220px;
      margin-top: -40px;
    }
  }
</style>
